import React from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { Field, FieldProps } from 'formik'
import { LargeUser as UserIcon } from '@urbaninfrastructure/react-icons'
import {
  Box,
  H2,
  Text,
  Input,
  FormField,
  PasswordInput,
  validateAdministratorName,
} from '../../../components'
import { isEmail } from '../../../lib/util'
import { validationMessages } from '../../../lib/messages'
import { labelMessages } from '../messages'
import { Values } from '../types'

const PASSWORD_MIN_LENGTH = 6

function validateAdministratorEmail(value) {
  let error
  if (!value) {
    error = 'EMAIL_REQUIRED'
  } else if (!isEmail(value)) {
    error = 'EMAIL_INVALID'
  }
  return error
}

function validateAdministratorPassword(value) {
  let error
  if (!value) {
    error = 'PASSWORD_REQUIRED'
  }
  if (value.length <= PASSWORD_MIN_LENGTH) {
    error = 'PASSWORD_MIN_LENGTH'
  }
  return error
}

const messages = defineMessages({
  description: {
    id: 'modules.Registration.AdministratorStep.description',
    defaultMessage:
      'By purchasing these vouchers, you are the default administrator of your account. You can create additional administrators once you have signed up.',
  },
  NAME_REQUIRED: {
    id: 'modules.Registration.AdministratorStep.NAME_REQUIRED',
    defaultMessage: 'Name is required',
  },
  PASSWORD_REQUIRED: {
    id: 'modules.Registration.AdministratorStep.PASSWORD_REQUIRED',
    defaultMessage: 'Password is required',
  },
  PASSWORD_MIN_LENGTH: {
    id: 'modules.Registration.AdministratorStep.PASSWORD_MIN_LENGTH',
    defaultMessage: 'Enter at least {minLength} characters',
  },
})

const AdministratorStep = () => {
  const intl = useIntl()
  return (
    <Box mb="md" textAlign="center">
      <Box mb="sm">
        <UserIcon size="80px" color="primary" />
        <H2 mb={0} typoStyle="md" mt="sm">
          <FormattedMessage
            id="modules.Registration.AdministratorStep.heading"
            defaultMessage="Your details"
          />
        </H2>
        <Box maxWidth="300px" mx="auto">
          <Text typoStyle="xxs" mt="xs" mute>
            <FormattedMessage {...messages.description} />
          </Text>
        </Box>
      </Box>
      <Box textAlign="left">
        <Field name="administratorName" validate={validateAdministratorName}>
          {({
            field,
            form,
          }: FieldProps<Values['administratorName'], Values>) => {
            const error = form.touched[field.name] && form.errors[field.name]
            const errorMessage = error && validationMessages[error] && (
              <FormattedMessage {...validationMessages[error]} />
            )
            return (
              <FormField
                label={intl.formatMessage(labelMessages.administratorName)}
                errorMessage={errorMessage}
              >
                <Input id={field.name} {...field} alternative autoFocus />
              </FormField>
            )
          }}
        </Field>
        <Field name="administratorEmail" validate={validateAdministratorEmail}>
          {({ field, form }) => {
            const error = form.touched[field.name] && form.errors[field.name]
            const errorMessage = error && validationMessages[error] && (
              <FormattedMessage {...validationMessages[error]} />
            )
            return (
              <Box mb="sm">
                <FormField
                  label={intl.formatMessage(labelMessages.administratorEmail)}
                  errorMessage={errorMessage}
                >
                  <Input id={field.name} {...field} alternative type="email" />
                </FormField>
              </Box>
            )
          }}
        </Field>
        <Field
          name="administratorPassword"
          validate={validateAdministratorPassword}
        >
          {({ field, form }) => {
            const error = form.touched[field.name] && form.errors[field.name]
            const errorMessage = error && messages[error] && (
              <FormattedMessage
                {...messages[error]}
                values={{ minLength: PASSWORD_MIN_LENGTH }}
              />
            )
            return (
              <Box mb="sm">
                <FormField
                  label={intl.formatMessage(
                    labelMessages.administratorPassword
                  )}
                  errorMessage={errorMessage}
                >
                  <PasswordInput id={field.name} {...field} alternative />
                </FormField>
              </Box>
            )
          }}
        </Field>
      </Box>
    </Box>
  )
}

export default AdministratorStep
